/**
 * FoodPulseGuides — cornerstone editorial index.
 *
 * Renders the ten authoritative guides the SEO audit called out as the
 * baseline content program. Lives at `/food-pulse/guides` and links to
 * per-guide detail pages. Every card carries a status badge (Available
 * now / Limited beta / Growing database) so nothing appears more mature
 * than it actually is.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import FoodPulseTopNav from "@/components/FoodPulseTopNav";
import FoodPulseJourneyNav from "@/components/FoodPulseJourneyNav";
import FeatureStatusBadge from "@/components/FeatureStatusBadge";

const JOURNEY_COLOR = {
  discover:   "bg-emerald-50 text-emerald-800 ring-emerald-200",
  understand: "bg-sky-50 text-sky-800 ring-sky-200",
  nearby:     "bg-amber-50 text-amber-800 ring-amber-200",
  share:      "bg-rose-50 text-rose-800 ring-rose-200",
  cook:       "bg-violet-50 text-violet-800 ring-violet-200",
};

export default function FoodPulseGuides() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await http.get("/food-pulse/guides");
        setItems(r.data?.items || []);
      } catch { /* fall through to empty */ }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="fp-guides-page">
      <SeoHead
        section="food-pulse"
        title="Cornerstone Guides — FoodPulse"
        description="Authoritative, source-linked guides on food waste, safe donation, date labels, freezing, composting, gleaning, allergens and more."
      />
      <FoodPulseTopNav />
      <FoodPulseJourneyNav />

      <section className="mx-auto max-w-5xl px-4 py-10">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
          <BookOpen className="h-3.5 w-3.5" /> FoodPulse cornerstone guides
        </div>
        <h1 className="mt-2 font-serif text-3xl text-slate-900 sm:text-4xl">
          Everything worth knowing, in ten guides.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">
          Written by our editors, source-linked, and reviewed on a rolling
          schedule. Skim the summary, or dive into the full guide.
        </p>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading guides…
          </div>
        ) : (
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="fp-guides-list">
            {items.map((g) => (
              <li key={g.slug} data-testid={`fp-guide-${g.slug}`}>
                <Link
                  to={`/food-pulse/guides/${g.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-emerald-300 hover:shadow-md"
                >
                  {g.hero_image_url ? (
                    <img
                      src={g.hero_image_url}
                      alt=""
                      className="h-40 w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className={`flex h-24 w-full items-center justify-center ${JOURNEY_COLOR[g.journey] || "bg-slate-50 text-slate-500"}`}>
                      <BookOpen className="h-8 w-8 opacity-60" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-start justify-between gap-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ring-1 ${JOURNEY_COLOR[g.journey] || "bg-slate-100 text-slate-700 ring-slate-200"}`}>
                        {g.journey}
                      </span>
                      <FeatureStatusBadge status={g.status} testid={`fp-guide-status-${g.slug}`} />
                    </div>
                    <h2 className="mt-3 font-serif text-lg text-slate-900 group-hover:text-emerald-800">
                      {g.title}
                    </h2>
                    <p className="mt-2 flex-1 text-xs text-slate-600 sm:text-sm">
                      {g.summary}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                      <span>Last reviewed {g.last_reviewed}</span>
                      <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                        Read guide <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
